/**
 * Vision Benefit Definitions
 *
 * Uses builder functions for standard type/amount pairs and type/multiple-amounts patterns.
 * Enum values provided via typeEnumValues parameter using OpenAPI types.
 */

import {
  BaseBenefitPlanPolicyPeriod,
  EmployerContributionEnum,
  FundingTypeEnum,
  RateGuaranteeTypeEnum,
  RfpAnnualOpenEnrollmentEnum,
  RfpFramesDiscountsRuleType,
  RfpVisionCopayAllowanceRuleType,
  RfpVisionCopayFullAllowanceRuleType,
  RfpVisionCopayRuleType,
  RfpVisionSpecialCopayAllowanceRuleType,
  VisionBenefitFrequency,
  addNotDisclosedAndNotOffered,
  extendEnumWithNotDisclosedAndNotOffered,
} from "@platform/openapi-types";

import { planNameRow } from "../shared/definitions/planNameRow";
import { BenefitDefinitions } from "../../benefits/benefit.types";
import {
  createEnumFormatter,
  createValueFormatter,
} from "../../components/PlanEditor/utilities/enumFormatters";
import * as sharedCellSpecs from "../shared/cellSpecs/common";
import {
  buildCommissionBenefits,
  buildTypeAmountBenefitPair,
  buildTypeMultipleAmountsBenefit,
} from "../shared/definitions/builders";
import { employerContributionLabels } from "../shared/definitions/customLabels";

// Pre-build commission definitions to override isRequired on type fields
const commissionDefs = buildCommissionBenefits();

const copay = "Copay Amount";
const allowance = "Allowance Amount";
const dollarAmountAllowance = "Dollar Amount Allowance";
const dollarAmountAllowanceWithoutCopay =
  "Dollar Amount Allowance Without Copay";
const notIncluded = "Not Included";

// Vision's mapping rules render the notIncluded contribution value as
// "Not Disclosed" rather than the default label. Scoped to Vision rather than
// the shared employerContributionLabels so other LOCs' Employer Contribution
// field keeps the default "Not Included" label.
const visionEmployerContributionLabels = {
  ...employerContributionLabels,
  notIncluded: "Not Disclosed",
};

// Extended (non-v5) fields (CD-1661/CD-1983). Backend fields are plain
// strings/floats for now (RFC-004), not yet enums, so type fields are text
// rather than dropdowns and amount fields aren't gated by editableWhen.
const premiumProgressiveCoreTypeLabels = {
  dollarAmountAllowance,
  dollarAmountAllowanceWithoutCopay,
  dollarAmountAllowanceWithCopay: "Dollar Amount Allowance with Copay",
  notIncluded,
};

const conventionalContactCoreTypeLabels = {
  dollarAmountAllowance,
  dollarAmountAllowanceWithoutCopay,
  dollarAmountAllowanceWithAdditionalDiscount:
    "Dollar Amount Allowance with Additional Discount",
  notIncluded,
};

const disposableContactCoreTypeLabels = {
  dollarAmountAllowance,
  dollarAmountAllowanceWithoutCopay,
  allowanceInLieuOfGlasses: "Allowance in Lieu of Glasses",
  allowanceInLieuOfGlassesWithCopay: "Allowance in Lieu of Glasses with Copay",
  notIncluded,
};

const contactsMedNecCoreTypeLabels = {
  coveredInFullAfterCopay: "Covered In Full After Copay",
  coveredInFull: "Covered In Full",
  dollarAmountAllowance,
  notIncluded,
};

const progressiveLensesCoreTypeLabels = {
  coveredInFullAfterCopay: "Covered In Full After Copay",
  dollarAmountAllowance,
  dollarAmountAllowanceWithoutCopay,
  notIncluded,
};

export const benefits: BenefitDefinitions = {
  currentCarrierName: {
    label: "Current Carrier",
    description: "Name of the current insurance carrier",
    cell: sharedCellSpecs.currentCarrierName,
    isRequired: true,
    networkDependent: false,
    benefitPlanLevel: true,
    editableWhen: {
      dependsOn: "policyPeriod",
      disabledValues: [
        BaseBenefitPlanPolicyPeriod.newBusiness,
        BaseBenefitPlanPolicyPeriod.alternate,
      ],
    },
  },
  ...commissionDefs,
  currentCommissionType: {
    ...commissionDefs["currentCommissionType"],
    isRequired: true,
    editableWhen: {
      dependsOn: "policyPeriod",
      disabledValues: [
        BaseBenefitPlanPolicyPeriod.newBusiness,
        BaseBenefitPlanPolicyPeriod.alternate,
      ],
    },
  },
  requestedCommissionType: {
    ...commissionDefs["requestedCommissionType"],
    isRequired: true,
  },
  eligibilityClasses: {
    label: "Eligibility Class",
    mpConfigKey: "eligibility_class",
    description: "Benefit classes assigned to this benefit plan group",
    cell: sharedCellSpecs.eligibilityClasses,
    benefitPlanLevel: true,
    isRequired: true,
  },
  name: planNameRow,
  fundingType: {
    label: "Funding Type",
    description: "Funding arrangement for the benefit plan",
    cell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        allowTyping: true,
        values: Object.values(FundingTypeEnum),
        formatValue: createEnumFormatter(),
      },
      valueFormatter: createValueFormatter(),
    },
    isRequired: true,
    requiredOnCurrentCoverageOnly: true,
    networkDependent: false,
    benefitPlanLevel: true,
  },
  networkName: {
    label: "Network",
    description: "Name of the vision network",
    cell: sharedCellSpecs.networkName,
    networkDependent: false,
    isRequired: true,
  },
  ...buildTypeAmountBenefitPair({
    prefix: "examCopay",
    typeLabel: "Exam Copay",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosedAndNotOffered(
      RfpVisionCopayRuleType,
    ),
    amountCell: sharedCellSpecs.dollarAmount,
    typeDescription: "Type of exam copay structure",
    amountDescription: "Exam copay dollar amount",
  }),
  ...buildTypeAmountBenefitPair({
    prefix: "materialsCopay",
    typeLabel: "Materials Copay",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosedAndNotOffered(
      RfpVisionCopayRuleType,
    ),
    amountCell: sharedCellSpecs.dollarAmount,
    typeDescription: "Type of materials copay structure",
    amountDescription: "Materials copay dollar amount",
  }),
  examFrequency: {
    label: "Frequency - Exam",
    description: "How often eye exams are covered",
    cell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: extendEnumWithNotDisclosedAndNotOffered(VisionBenefitFrequency),
      },
    },
    networkDependent: false,
  },
  lensesFrequency: {
    label: "Frequency - Lenses",
    description: "How often lenses are covered",
    cell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: extendEnumWithNotDisclosedAndNotOffered(VisionBenefitFrequency),
      },
    },
    networkDependent: false,
  },
  framesFrequency: {
    label: "Frequency - Frames",
    description: "How often frames are covered",
    cell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: extendEnumWithNotDisclosedAndNotOffered(VisionBenefitFrequency),
      },
    },
    networkDependent: false,
  },
  ...buildTypeMultipleAmountsBenefit({
    prefix: "singleLenses",
    typeLabel: "Single Lenses - After Copay/Allowance",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosedAndNotOffered(
      RfpVisionCopayFullAllowanceRuleType,
    ),
    typeDescription: "Type of single lenses benefit structure",
    amountFields: [
      {
        suffix: "CopayAmount",
        label: copay,
        cell: sharedCellSpecs.dollarAmount,
        description: "Single lenses copay amount",
        disabledValues: addNotDisclosedAndNotOffered([
          RfpVisionCopayFullAllowanceRuleType.dollarAmountAllowance,
          RfpVisionCopayFullAllowanceRuleType.coveredInFull,
          RfpVisionCopayFullAllowanceRuleType.notIncluded,
        ]),
      },
      {
        suffix: "AllowanceAmount",
        label: allowance,
        cell: sharedCellSpecs.dollarAmount,
        description: "Single lenses allowance amount",
        disabledValues: addNotDisclosedAndNotOffered([
          RfpVisionCopayFullAllowanceRuleType.coveredInFullAfterCopay,
          RfpVisionCopayFullAllowanceRuleType.coveredInFull,
          RfpVisionCopayFullAllowanceRuleType.notIncluded,
        ]),
      },
    ],
  }),
  ...buildTypeMultipleAmountsBenefit({
    prefix: "bifocalLenses",
    typeLabel: "Bifocal Lenses - After Copay/Allowance",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosedAndNotOffered(
      RfpVisionCopayFullAllowanceRuleType,
    ),
    typeDescription: "Type of bifocal lenses benefit structure",
    amountFields: [
      {
        suffix: "CopayAmount",
        label: copay,
        cell: sharedCellSpecs.dollarAmount,
        description: "Bifocal lenses copay amount",
        disabledValues: addNotDisclosedAndNotOffered([
          RfpVisionCopayFullAllowanceRuleType.dollarAmountAllowance,
          RfpVisionCopayFullAllowanceRuleType.coveredInFull,
          RfpVisionCopayFullAllowanceRuleType.notIncluded,
        ]),
      },
      {
        suffix: "AllowanceAmount",
        label: allowance,
        cell: sharedCellSpecs.dollarAmount,
        description: "Bifocal lenses allowance amount",
        disabledValues: addNotDisclosedAndNotOffered([
          RfpVisionCopayFullAllowanceRuleType.coveredInFullAfterCopay,
          RfpVisionCopayFullAllowanceRuleType.coveredInFull,
          RfpVisionCopayFullAllowanceRuleType.notIncluded,
        ]),
      },
    ],
  }),
  ...buildTypeMultipleAmountsBenefit({
    prefix: "trifocalLenses",
    typeLabel: "Trifocal Lenses - After Copay/Allowance",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosedAndNotOffered(
      RfpVisionCopayFullAllowanceRuleType,
    ),
    typeDescription: "Type of trifocal lenses benefit structure",
    amountFields: [
      {
        suffix: "CopayAmount",
        label: copay,
        cell: sharedCellSpecs.dollarAmount,
        description: "Trifocal lenses copay amount",
        disabledValues: addNotDisclosedAndNotOffered([
          RfpVisionCopayFullAllowanceRuleType.dollarAmountAllowance,
          RfpVisionCopayFullAllowanceRuleType.coveredInFull,
          RfpVisionCopayFullAllowanceRuleType.notIncluded,
        ]),
      },
      {
        suffix: "AllowanceAmount",
        label: allowance,
        cell: sharedCellSpecs.dollarAmount,
        description: "Trifocal lenses allowance amount",
        disabledValues: addNotDisclosedAndNotOffered([
          RfpVisionCopayFullAllowanceRuleType.coveredInFullAfterCopay,
          RfpVisionCopayFullAllowanceRuleType.coveredInFull,
          RfpVisionCopayFullAllowanceRuleType.notIncluded,
        ]),
      },
    ],
  }),
  ...buildTypeMultipleAmountsBenefit({
    prefix: "lenticularLenses",
    mpConfigKey: "lenticular_lenses_after_copay_allowance",
    typeLabel: "Lenticular Lenses - After Copay/Allowance",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosedAndNotOffered(
      RfpVisionCopayFullAllowanceRuleType,
    ),
    typeDescription: "Type of lenticular lenses benefit structure",
    amountFields: [
      {
        suffix: "CopayAmount",
        label: copay,
        cell: sharedCellSpecs.dollarAmount,
        description: "Lenticular lenses copay amount",
        disabledValues: addNotDisclosedAndNotOffered([
          RfpVisionCopayFullAllowanceRuleType.dollarAmountAllowance,
          RfpVisionCopayFullAllowanceRuleType.coveredInFull,
          RfpVisionCopayFullAllowanceRuleType.notIncluded,
        ]),
      },
      {
        suffix: "AllowanceAmount",
        label: allowance,
        cell: sharedCellSpecs.dollarAmount,
        description: "Lenticular lenses allowance amount",
        disabledValues: addNotDisclosedAndNotOffered([
          RfpVisionCopayFullAllowanceRuleType.coveredInFullAfterCopay,
          RfpVisionCopayFullAllowanceRuleType.coveredInFull,
          RfpVisionCopayFullAllowanceRuleType.notIncluded,
        ]),
      },
    ],
  }),
  ...buildTypeMultipleAmountsBenefit({
    prefix: "standardProgressive",
    mpConfigKey: "standard_progressive",
    typeLabel: "Standard Progressive",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosedAndNotOffered(
      RfpVisionCopayAllowanceRuleType,
    ),
    typeDescription: "Type of standard progressive lenses benefit structure",
    amountFields: [
      {
        suffix: "CopayAmount",
        label: copay,
        cell: sharedCellSpecs.dollarAmount,
        description: "Standard progressive copay amount",
        disabledValues: addNotDisclosedAndNotOffered([
          RfpVisionCopayAllowanceRuleType.dollarAmountAllowance,
          RfpVisionCopayAllowanceRuleType.dollarAmountAllowanceWithoutCopay,
          RfpVisionCopayAllowanceRuleType.notIncluded,
        ]),
      },
      {
        suffix: "AllowanceAmount",
        label: allowance,
        cell: sharedCellSpecs.dollarAmount,
        description: "Standard progressive allowance amount",
      },
    ],
  }),
  ...buildTypeMultipleAmountsBenefit({
    prefix: "premiumProgressive",
    mpOnly: true,
    mpConfigKey: "premium_progressive",
    typeLabel: "Premium Progressive",
    typeCell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: addNotDisclosedAndNotOffered(
          Object.keys(premiumProgressiveCoreTypeLabels),
        ),
        formatValue: createEnumFormatter(premiumProgressiveCoreTypeLabels),
      },
      valueFormatter: createValueFormatter(premiumProgressiveCoreTypeLabels),
    },
    typeDescription: "Type of premium progressive lenses benefit structure",
    amountFields: [
      {
        suffix: "CopayAmount",
        label: copay,
        cell: sharedCellSpecs.dollarAmount,
        description: "Premium progressive lenses copay amount",
        disabledValues: addNotDisclosedAndNotOffered([
          "dollarAmountAllowance",
          "dollarAmountAllowanceWithoutCopay",
          "notIncluded",
        ]),
      },
      {
        suffix: "AllowanceAmount",
        label: allowance,
        cell: sharedCellSpecs.dollarAmount,
        description: "Premium progressive lenses allowance amount",
      },
    ],
  }),
  ...buildTypeMultipleAmountsBenefit({
    prefix: "standardFrames",
    typeLabel: "Standard Frames Allowance",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosedAndNotOffered(
      RfpVisionSpecialCopayAllowanceRuleType,
    ),
    typeDescription: "Type of standard frames benefit structure",
    amountFields: [
      {
        suffix: "CopayAmount",
        label: copay,
        cell: sharedCellSpecs.dollarAmount,
        description: "Standard frames copay amount",
        disabledValues: addNotDisclosedAndNotOffered([
          RfpVisionSpecialCopayAllowanceRuleType.dollarAmountAllowance,
          RfpVisionSpecialCopayAllowanceRuleType.dollarAmountAllowanceWithoutCopay,
          RfpVisionSpecialCopayAllowanceRuleType.notIncluded,
        ]),
      },
      {
        suffix: "AllowanceAmount",
        label: allowance,
        cell: sharedCellSpecs.dollarAmount,
        description: "Standard frames allowance amount",
      },
    ],
  }),
  ...buildTypeMultipleAmountsBenefit({
    prefix: "conventionalContact",
    mpOnly: true,
    mpConfigKey: "conventional",
    typeLabel: "Conventional",
    typeCell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: addNotDisclosedAndNotOffered(
          Object.keys(conventionalContactCoreTypeLabels),
        ),
        formatValue: createEnumFormatter(conventionalContactCoreTypeLabels),
      },
      valueFormatter: createValueFormatter(conventionalContactCoreTypeLabels),
    },
    typeDescription: "Type of conventional contact lens benefit structure",
    amountFields: [
      {
        suffix: "AdditionalDiscountAmount",
        label: "Additional Discount Amount",
        cell: sharedCellSpecs.percentageAmount,
        description: "Conventional contact lens additional discount amount",
        disabledValues: addNotDisclosedAndNotOffered([
          "dollarAmountAllowance",
          "dollarAmountAllowanceWithoutCopay",
          "notIncluded",
        ]),
      },
      {
        suffix: "AllowanceAmount",
        label: allowance,
        cell: sharedCellSpecs.dollarAmount,
        description: "Conventional contact lens allowance amount",
        disabledValues: addNotDisclosedAndNotOffered(["notIncluded"]),
      },
    ],
  }),
  ...buildTypeMultipleAmountsBenefit({
    prefix: "disposableContact",
    mpOnly: true,
    mpConfigKey: "disposable",
    typeLabel: "Disposable",
    typeCell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: addNotDisclosedAndNotOffered(
          Object.keys(disposableContactCoreTypeLabels),
        ),
        formatValue: createEnumFormatter(disposableContactCoreTypeLabels),
      },
      valueFormatter: createValueFormatter(disposableContactCoreTypeLabels),
    },
    typeDescription: "Type of disposable contact lens benefit structure",
    amountFields: [
      {
        suffix: "CopayAmount",
        label: copay,
        cell: sharedCellSpecs.dollarAmount,
        description: "Disposable contact lens copay amount",
        disabledValues: addNotDisclosedAndNotOffered([
          "dollarAmountAllowance",
          "dollarAmountAllowanceWithoutCopay",
          "allowanceInLieuOfGlasses",
          "notIncluded",
        ]),
      },
      {
        suffix: "AllowanceAmount",
        label: allowance,
        cell: sharedCellSpecs.dollarAmount,
        description: "Disposable contact lens allowance amount",
        disabledValues: addNotDisclosedAndNotOffered(["notIncluded"]),
      },
    ],
  }),
  ...buildTypeMultipleAmountsBenefit({
    prefix: "contactLensElective",
    mpConfigKey: "contacts_elective",
    typeLabel: "Contacts (Elective)",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosedAndNotOffered(
      RfpVisionSpecialCopayAllowanceRuleType,
    ),
    typeDescription: "Type of contact (elective) benefit structure",
    amountFields: [
      {
        suffix: "CopayAmount",
        label: copay,
        cell: sharedCellSpecs.dollarAmount,
        description: "Contact (elective) copay amount",
        disabledValues: addNotDisclosedAndNotOffered([
          RfpVisionSpecialCopayAllowanceRuleType.dollarAmountAllowance,
          RfpVisionSpecialCopayAllowanceRuleType.dollarAmountAllowanceWithoutCopay,
          RfpVisionSpecialCopayAllowanceRuleType.notIncluded,
        ]),
      },
      {
        suffix: "AllowanceAmount",
        label: allowance,
        cell: sharedCellSpecs.dollarAmount,
        description: "Contact (elective) allowance amount",
      },
    ],
  }),
  ...buildTypeMultipleAmountsBenefit({
    prefix: "contactLensMedNec",
    mpOnly: true,
    mpConfigKey: "contacts_med_nec",
    typeLabel: "Contacts (Med Nec)",
    typeCell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: addNotDisclosedAndNotOffered(
          Object.keys(contactsMedNecCoreTypeLabels),
        ),
        formatValue: createEnumFormatter(contactsMedNecCoreTypeLabels),
      },
      valueFormatter: createValueFormatter(contactsMedNecCoreTypeLabels),
    },
    typeDescription:
      "Type of medically-necessary contact lens benefit structure",
    amountFields: [
      {
        suffix: "CopayAmount",
        label: copay,
        cell: sharedCellSpecs.dollarAmount,
        description: "Medically-necessary contact lens copay amount",
        disabledValues: addNotDisclosedAndNotOffered([
          "dollarAmountAllowance",
          "coveredInFull",
          "notIncluded",
        ]),
      },
      {
        suffix: "AllowanceAmount",
        label: allowance,
        cell: sharedCellSpecs.dollarAmount,
        description: "Medically-necessary contact lens allowance amount",
        disabledValues: addNotDisclosedAndNotOffered([
          "coveredInFullAfterCopay",
          "coveredInFull",
          "notIncluded",
        ]),
      },
    ],
  }),
  ...buildTypeAmountBenefitPair({
    prefix: "additionalDiscountsOnFrames",
    typeLabel: "Additional Discounts on Frames",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosedAndNotOffered(
      RfpFramesDiscountsRuleType,
    ),
    amountCell: sharedCellSpecs.dollarAmount,
    typeDescription: "Type of additional frames discount",
    amountDescription: "Discount amount (percentage or dollar)",
    amountCellVariants: {
      options: {
        upToDollarAmountOff: sharedCellSpecs.dollarAmount,
        upToPercentageOff: sharedCellSpecs.percentageAmount,
      },
      default: sharedCellSpecs.dollarAmount,
    },
  }),
  ...buildTypeMultipleAmountsBenefit({
    prefix: "contactLensExam",
    mpConfigKey: "contact_lens_exam",
    typeLabel: "Contact Lens Exam",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosedAndNotOffered(
      RfpVisionCopayAllowanceRuleType,
    ),
    typeDescription: "Type of contact lens exam benefit structure",
    amountFields: [
      {
        suffix: "CopayAmount",
        label: copay,
        cell: sharedCellSpecs.dollarAmount,
        description: "Contact lens exam copay amount",
        disabledValues: addNotDisclosedAndNotOffered([
          RfpVisionCopayAllowanceRuleType.dollarAmountAllowance,
          RfpVisionCopayAllowanceRuleType.dollarAmountAllowanceWithoutCopay,
          RfpVisionCopayAllowanceRuleType.notIncluded,
        ]),
      },
      {
        suffix: "AllowanceAmount",
        label: allowance,
        cell: sharedCellSpecs.dollarAmount,
        description: "Contact lens exam allowance amount",
        disabledValues: addNotDisclosedAndNotOffered([
          RfpVisionCopayAllowanceRuleType.coveredInFullAfterCopay,
          RfpVisionCopayAllowanceRuleType.notIncluded,
        ]),
      },
    ],
  }),
  ...buildTypeMultipleAmountsBenefit({
    prefix: "progressiveLenses",
    mpOnly: true,
    mpConfigKey: "progressive_lenses",
    typeLabel: "Progressive Lenses",
    typeCell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: addNotDisclosedAndNotOffered(
          Object.keys(progressiveLensesCoreTypeLabels),
        ),
        formatValue: createEnumFormatter(progressiveLensesCoreTypeLabels),
      },
      valueFormatter: createValueFormatter(progressiveLensesCoreTypeLabels),
    },
    typeDescription: "Type of progressive lenses benefit structure",
    amountFields: [
      {
        suffix: "CopayAmount",
        label: copay,
        cell: sharedCellSpecs.dollarAmount,
        description: "Progressive lenses copay amount",
        disabledValues: addNotDisclosedAndNotOffered([
          "dollarAmountAllowance",
          "dollarAmountAllowanceWithoutCopay",
          "notIncluded",
        ]),
      },
      {
        suffix: "AllowanceAmount",
        label: allowance,
        cell: sharedCellSpecs.dollarAmount,
        description: "Progressive lenses allowance amount",
        disabledValues: addNotDisclosedAndNotOffered([
          "coveredInFullAfterCopay",
          "notIncluded",
        ]),
      },
    ],
  }),
  annualOpenEnrollment: {
    label: "Annual Open Enrollment",
    mpConfigKey: "annual_open_enrollment",
    description: "Type of annual open enrollment available",
    cell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: extendEnumWithNotDisclosedAndNotOffered(
          RfpAnnualOpenEnrollmentEnum,
        ),
      },
    },
    networkDependent: false,
  },
  employerContribution: {
    label: "Employer Contribution",
    description: "Level of employer contribution",
    cell: {
      ...sharedCellSpecs.employerContribution,
      cellEditorParams: {
        ...sharedCellSpecs.employerContribution.cellEditorParams,
        values: Object.values(EmployerContributionEnum),
        formatValue: createEnumFormatter(visionEmployerContributionLabels),
      },
      valueFormatter: createValueFormatter(visionEmployerContributionLabels),
    },
    networkDependent: false,
    isRequired: true,
  },
  // Unlike its sibling extended fields above, eligibilityHours has a
  // confirmed dropdown value list (CD-2325).
  eligibilityHours: {
    mpOnly: true,
    label: "Eligibility Hours",
    mpConfigKey: "eligibility_hours",
    description: "Minimum hours worked per week to be eligible for the plan",
    cell: sharedCellSpecs.eligibilityHoursDropdown,
    networkDependent: false,
  },
  participationRequirements: {
    label: "Participation Requirements",
    description: "Participation requirement details for the benefit plan",
    cell: sharedCellSpecs.text,
    networkDependent: false,
    benefitPlanLevel: true,
  },
  ...buildTypeAmountBenefitPair({
    prefix: "rateGuarantee",
    typeLabel: "Rate Guarantee",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: Object.keys(RateGuaranteeTypeEnum),
    amountCell: sharedCellSpecs.numberField,
    typeDescription:
      "Time frame in which rates are guaranteed to stay the same",
    amountDescription: "Number of months or years the rate is guaranteed",
    amountDefaultValue: 1,
    disabledValues: [
      RateGuaranteeTypeEnum.notIncluded,
      RateGuaranteeTypeEnum.notOffered,
    ],
  }),
} as const;
